import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/App";
import {
  MapPin, Plus, Search, Loader2, Send, Eye, FileText,
  CheckCircle, XCircle, AlertTriangle, Upload, Plane, DollarSign, Receipt,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { FALLBACK_TRIPS } from "@/data/dummyData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const emptyTrip = {
  employee_name: "", employee_department: "", employee_appointment: "",
  trip_type: "domestic", destination: "", purpose: "",
  start_date: "", end_date: "",
  requires_accommodation: false, requires_flight: false,
  estimated_cost: 0, currency: "ZAR", comment_on_preference: "",
};

const STATUS_MAP = {
  draft: "Draft", pending_travel_desk: "Pending Travel Desk",
  travel_desk_booked: "Travel Desk Booked", claim_submitted: "Claim Submitted",
  manager_recommended: "Manager Recommended", finance_approved: "Finance Approved",
  disbursed: "Disbursed", in_progress: "In Progress",
  recon_submitted: "Recon Submitted", recon_ai_verified: "Recon AI Verified",
  recon_manager_recommended: "Recon Manager Rec.", recon_finance_approved: "Recon Approved",
  closed: "Closed", cancelled: "Cancelled",
};

const STATUS_COLOR = {
  draft: "text-slate-500 bg-slate-100",
  pending_travel_desk: "text-amber-600 bg-amber-50",
  travel_desk_booked: "text-blue-600 bg-blue-50",
  claim_submitted: "text-purple-600 bg-purple-50",
  manager_recommended: "text-indigo-600 bg-indigo-50",
  finance_approved: "text-cyan-600 bg-cyan-50",
  disbursed: "text-green-600 bg-green-50",
  in_progress: "text-sky-600 bg-sky-50",
  recon_submitted: "text-orange-600 bg-orange-50",
  recon_ai_verified: "text-teal-600 bg-teal-50",
  recon_manager_recommended: "text-indigo-600 bg-indigo-50",
  recon_finance_approved: "text-emerald-600 bg-emerald-50",
  closed: "text-green-700 bg-green-100",
  cancelled: "text-red-600 bg-red-50",
};

export default function TravellingAdminMyTrips() {
  const { user: authUser } = useAuth();
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [structures, setStructures] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyTrip });
  const [saving, setSaving] = useState(false);
  const [selectedTrip, setSelectedTrip] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [booking, setBooking] = useState(null);
  const [claim, setClaim] = useState(null);
  const [disbursement, setDisbursement] = useState(null);
  const [recon, setRecon] = useState(null);
  // Claim dialog
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimForm, setClaimForm] = useState({ gl_account: "", daily_s_and_t_amount: 0, total_claim_amount: 0 });
  // Recon dialog
  const [reconOpen, setReconOpen] = useState(false);
  const [reconForm, setReconForm] = useState({ total_amount_used: 0, total_amount_returned: 0, original_currency_amount: 0, conversion_rate: 0, proof_of_return_url: "" });

  const fetchTrips = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (search) params.search = search;
      const res = await axios.get(`${API}/travelling/requests`, { params, headers: getHeaders() });
      setTrips(res.data.length > 0 ? res.data : FALLBACK_TRIPS);
    } catch { setTrips(FALLBACK_TRIPS); toast.error("Failed to load trips. Showing demo data."); }
    setLoading(false);
  }, [filterStatus, search]);

  useEffect(() => { fetchTrips(); }, [fetchTrips]);

  const openDetail = async (trip) => {
    setSelectedTrip(trip);
    setBooking(null); setClaim(null); setDisbursement(null); setRecon(null);
    try {
      const [bRes, cRes, dRes, rRes] = await Promise.all([
        axios.get(`${API}/travelling/bookings?request_id=${trip.id}`, { headers: getHeaders() }).catch(() => null),
        axios.get(`${API}/travelling/claims?request_id=${trip.id}`, { headers: getHeaders() }).catch(() => null),
        axios.get(`${API}/travelling/disbursements?request_id=${trip.id}`, { headers: getHeaders() }).catch(() => null),
        axios.get(`${API}/travelling/reconciliations?request_id=${trip.id}`, { headers: getHeaders() }).catch(() => null),
      ]);
      if (bRes?.data?.length) setBooking(bRes.data[0]);
      if (cRes?.data?.length) setClaim(cRes.data[0]);
      if (dRes?.data?.length) setDisbursement(dRes.data[0]);
      if (rRes?.data?.length) setRecon(rRes.data[0]);
    } catch {}
    setDetailOpen(true);
  };

  const openNew = () => {
    setForm({ ...emptyTrip, employee_name: authUser?.name || "" });
    axios.get(`${API}/travelling/structures`, { headers: getHeaders() })
      .then((res) => setStructures(res.data))
      .catch(() => setStructures([]));
    setDialogOpen(true);
  };

  const handleCreate = async () => {
    if (!form.destination.trim()) { toast.error("Destination is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/travelling/requests`, form, { headers: getHeaders() });
      toast.success("Trip request created");
      setDialogOpen(false);
      fetchTrips();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    setSaving(false);
  };

  const handleSubmit = async (id) => {
    try {
      await axios.put(`${API}/travelling/requests/${id}/status?status=pending_travel_desk`, {}, { headers: getHeaders() });
      toast.success("Submitted to Travel Desk");
      fetchTrips();
      if (detailOpen) setDetailOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to submit"); }
  };

  const handleOverride = async (id) => {
    const url = prompt("Enter override letter/document URL:");
    if (!url) return;
    try {
      await axios.put(`${API}/travelling/requests/${id}/override-block?override_letter_url=${encodeURIComponent(url)}`, {}, { headers: getHeaders() });
      toast.success("Block overridden");
      setDetailOpen(false);
      fetchTrips();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const openClaimDialog = () => {
    setClaimForm({ gl_account: "", daily_s_and_t_amount: 0, total_claim_amount: 0 });
    setClaimOpen(true);
  };

  const handleSubmitClaim = async () => {
    if (!claimForm.gl_account) { toast.error("GL account is required"); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/travelling/claims`, { ...claimForm, request_id: selectedTrip.id }, { headers: getHeaders() });
      toast.success("Expense claim submitted");
      setClaimOpen(false);
      setDetailOpen(false);
      fetchTrips();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const openReconDialog = () => {
    setReconForm({ total_amount_used: 0, total_amount_returned: 0, original_currency_amount: 0, conversion_rate: 0, proof_of_return_url: "" });
    setReconOpen(true);
  };

  const handleSubmitRecon = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/travelling/reconciliations`, { ...reconForm, request_id: selectedTrip.id }, { headers: getHeaders() });
      toast.success("Reconciliation submitted");
      setReconOpen(false);
      setDetailOpen(false);
      fetchTrips();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
    setSaving(false);
  };

  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this trip request?")) return;
    try {
      await axios.put(`${API}/travelling/requests/${id}/status?status=cancelled`, {}, { headers: getHeaders() });
      toast.success("Trip cancelled");
      fetchTrips();
      if (detailOpen) setDetailOpen(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 bg-gradient-to-br from-sky-500 to-blue-600 rounded-xl flex items-center justify-center">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-['Barlow_Condensed'] text-3xl font-bold text-slate-800 tracking-wide">MY TRIPS</h1>
            <p className="text-slate-600">Your trip requests, claims & reconciliations</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 p-4 mb-6 flex items-center gap-3 flex-wrap">
        <Search className="w-4 h-4 text-slate-400" />
        <Input placeholder="Search trips..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-48 bg-app border-slate-300 rounded-none" />
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-40 bg-app border-slate-300 rounded-none"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent className="bg-white border-slate-300 rounded-none">
            <SelectItem value="all">All Statuses</SelectItem>
            {Object.entries(STATUS_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="flex-1" />
        <Button onClick={openNew} className="bg-gradient-to-r from-sky-500 to-blue-600 rounded-none text-white">
          <Plus className="w-4 h-4 mr-1" /> New Trip Request
        </Button>
      </div>

      {/* Trip List */}
      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-sky-500" /></div>
      ) : trips.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 p-16 text-center">
          <Plane className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No trips found. Create a new trip request to get started.</p>
        </div>
      ) : (
        <div className="bg-white border border-slate-200 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-app border-b border-slate-200">
              <tr>
                {["Destination", "Type", "Period", "Cost", "Status", ""].map((h) => (
                  <th key={h} className="text-left p-3 font-semibold text-slate-600 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {trips.map((t) => (
                <tr key={t.id} className="border-b border-slate-100 hover:bg-app">
                  <td className="p-3">
                    <p className="font-medium text-slate-800">{t.destination || "-"}</p>
                    <p className="text-xs text-slate-400">{t.purpose?.slice(0, 60)}</p>
                  </td>
                  <td className="p-3"><span className={`text-xs px-2 py-0.5 ${t.trip_type === "foreign" ? "text-blue-600 bg-blue-50" : "text-slate-600 bg-slate-100"}`}>{t.trip_type}</span></td>
                  <td className="p-3 text-xs text-slate-500">{t.start_date?.slice(0, 10) || "-"} → {t.end_date?.slice(0, 10) || "-"}</td>
                  <td className="p-3 text-xs text-slate-500">{t.currency} {t.estimated_cost?.toLocaleString()}</td>
                  <td className="p-3">
                    <span className={`text-xs px-2 py-0.5 font-medium ${STATUS_COLOR[t.status] || "text-slate-500 bg-slate-100"}`}>
                      {STATUS_MAP[t.status] || t.status}
                    </span>
                    {t.outstanding_balance_blocked && (
                      <span className="ml-1 text-xs text-red-600"><AlertTriangle className="w-3 h-3 inline" /></span>
                    )}
                  </td>
                  <td className="p-3">
                    <Button size="sm" variant="ghost" onClick={() => openDetail(t)} className="text-sky-600 hover:text-sky-700">
                      <Eye className="w-4 h-4" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* New Trip Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">New Trip Request</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {/* Employee Name */}
            <Input placeholder="Employee Name" value={form.employee_name} onChange={(e) => setForm(f => ({ ...f, employee_name: e.target.value }))} className="rounded-none bg-app border-slate-300" />

            {/* Appointment Number */}
            <Input placeholder="Appointment Number (AP-XXXXX)" value={form.employee_appointment} onChange={(e) => setForm(f => ({ ...f, employee_appointment: e.target.value }))} className="rounded-none bg-app border-slate-300" />

            {/* Department — dropdown of structures */}
            <Select value={form.employee_department} onValueChange={(v) => setForm(f => ({ ...f, employee_department: v }))}>
              <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Select your structure / department" /></SelectTrigger>
              <SelectContent className="bg-white border-slate-300 rounded-none">
                {structures.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>

            {/* Trip Type & Currency */}
            <div className="grid grid-cols-2 gap-3">
              <Select value={form.trip_type} onValueChange={(v) => setForm(f => ({ ...f, trip_type: v, currency: v === "foreign" ? "USD" : "ZAR" }))}>
                <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Trip Type" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  <SelectItem value="domestic">Domestic</SelectItem>
                  <SelectItem value="foreign">Foreign</SelectItem>
                </SelectContent>
              </Select>
              <Select value={form.currency} onValueChange={(v) => setForm(f => ({ ...f, currency: v }))}>
                <SelectTrigger className="rounded-none bg-app border-slate-300"><SelectValue placeholder="Currency" /></SelectTrigger>
                <SelectContent className="bg-white border-slate-300 rounded-none">
                  <SelectItem value="ZAR">ZAR</SelectItem>
                  <SelectItem value="USD">USD</SelectItem>
                  <SelectItem value="EUR">EUR</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Destination */}
            <Input placeholder="Destination" value={form.destination} onChange={(e) => setForm(f => ({ ...f, destination: e.target.value }))} className="rounded-none bg-app border-slate-300" />

            {/* Purpose */}
            <textarea placeholder="Purpose of travel" value={form.purpose} onChange={(e) => setForm(f => ({ ...f, purpose: e.target.value }))} className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={2} />

            {/* Requires Flight / Accommodation */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.requires_flight} onChange={(e) => setForm(f => ({ ...f, requires_flight: e.target.checked }))} /> Requires Flight</label>
              <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.requires_accommodation} onChange={(e) => setForm(f => ({ ...f, requires_accommodation: e.target.checked }))} /> Requires Accommodation</label>
            </div>

            {/* Period — Start / End Date */}
            <div className="grid grid-cols-2 gap-3">
              <Input type="date" placeholder="Start Date" value={form.start_date} onChange={(e) => setForm(f => ({ ...f, start_date: e.target.value }))} className="rounded-none bg-app border-slate-300" />
              <Input type="date" placeholder="End Date" value={form.end_date} onChange={(e) => setForm(f => ({ ...f, end_date: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            </div>

            {/* Comment on Preference — always last field before submit */}
            <textarea placeholder="Comment on preference (e.g. preferred flight times, accommodation type, special requirements)"
              value={form.comment_on_preference}
              onChange={(e) => setForm(f => ({ ...f, comment_on_preference: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-300 bg-app rounded-none text-sm" rows={3} />

            {/* Submit button */}
            <Button onClick={handleCreate} disabled={saving} className="w-full bg-sky-600 hover:bg-sky-500 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating...</> : <><Plus className="w-4 h-4 mr-2" /> Create Trip Request</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Trip Detail Dialog */}
      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedTrip && (
            <>
              <DialogHeader>
                <DialogTitle className="font-['Barlow_Condensed'] text-xl flex items-center gap-2">
                  {selectedTrip.destination || "Trip Detail"}
                  <span className={`text-xs px-2 py-0.5 font-medium ${STATUS_COLOR[selectedTrip.status] || ""}`}>{STATUS_MAP[selectedTrip.status]}</span>
                  {selectedTrip.outstanding_balance_blocked && (
                    <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 flex items-center gap-1">
                      <AlertTriangle className="w-3 h-3" /> Blocked
                    </span>
                  )}
                </DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                <div><span className="text-slate-500">Employee:</span> <span className="font-medium">{selectedTrip.employee_name}</span></div>
                <div><span className="text-slate-500">Department:</span> {selectedTrip.employee_department || "-"}</div>
                <div><span className="text-slate-500">Type:</span> {selectedTrip.trip_type}</div>
                <div><span className="text-slate-500">Currency:</span> {selectedTrip.currency}</div>
                <div><span className="text-slate-500">Period:</span> {selectedTrip.start_date?.slice(0,10)} → {selectedTrip.end_date?.slice(0,10)}</div>
                <div><span className="text-slate-500">Estimated Cost:</span> {selectedTrip.currency} {selectedTrip.estimated_cost?.toLocaleString()}</div>
                <div className="col-span-2"><span className="text-slate-500">Purpose:</span> {selectedTrip.purpose || "-"}</div>
                {selectedTrip.comment_on_preference && (
                  <div className="col-span-2"><span className="text-slate-500">Comment on Preference:</span> <span className="italic">{selectedTrip.comment_on_preference}</span></div>
                )}
              </div>

              {/* Booking Info */}
              {booking && (
                <div className="mt-4 p-3 bg-blue-50 border border-blue-200">
                  <h4 className="font-semibold text-sm text-blue-700 mb-2 flex items-center gap-1"><Building2 className="w-4 h-4" /> Travel Desk Booking</h4>
                  <div className="text-xs text-slate-600 space-y-1">
                    <p>Flight: {booking.flight_booked ? "✅ Booked" : "❌ Not booked"} — {booking.flight_details || "-"}</p>
                    <p>Accommodation: {booking.accommodation_booked ? "✅ Booked" : "❌ Not booked"} — {booking.accommodation_details || "-"}</p>
                    <p>Notes: {booking.other_notes || "-"}</p>
                  </div>
                </div>
              )}

              {/* Claim Info */}
              {claim && (
                <div className="mt-4 p-3 bg-purple-50 border border-purple-200">
                  <h4 className="font-semibold text-sm text-purple-700 mb-2 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Expense Claim</h4>
                  <div className="text-xs text-slate-600 space-y-1">
                    <p>GL Account: {claim.gl_account}</p>
                    <p>Daily S&T: {claim.daily_s_and_t_amount}</p>
                    <p>Total Claim: {claim.total_claim_amount}</p>
                    <p>Manager: <span className={claim.manager_recommendation === "recommended" ? "text-green-600" : claim.manager_recommendation === "declined" ? "text-red-600" : "text-amber-600"}>{claim.manager_recommendation}</span></p>
                    <p>Finance: <span className={claim.finance_approval === "approved" ? "text-green-600" : claim.finance_approval === "declined" ? "text-red-600" : "text-amber-600"}>{claim.finance_approval}</span></p>
                  </div>
                </div>
              )}

              {/* Disbursement Info */}
              {disbursement && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200">
                  <h4 className="font-semibold text-sm text-green-700 mb-2 flex items-center gap-1"><DollarSign className="w-4 h-4" /> Disbursement</h4>
                  <div className="text-xs text-slate-600 space-y-1">
                    <p>Amount: {disbursement.currency} {disbursement.amount_disbursed?.toLocaleString()}</p>
                    <p>Reference: {disbursement.payment_reference || "-"}</p>
                    <p>Notes: {disbursement.notes || "-"}</p>
                  </div>
                </div>
              )}

              {/* Reconciliation Info */}
              {recon && (
                <div className="mt-4 p-3 bg-orange-50 border border-orange-200">
                  <h4 className="font-semibold text-sm text-orange-700 mb-2 flex items-center gap-1"><Receipt className="w-4 h-4" /> Reconciliation</h4>
                  <div className="text-xs text-slate-600 space-y-1">
                    <p>Amount Used: {recon.total_amount_used} | Returned: {recon.total_amount_returned}</p>
                    <p>Over: <span className="text-green-600">{recon.over_amount}</span> | Under: <span className="text-red-600">{recon.under_amount}</span></p>
                    <p>AI Verified: {recon.ai_verified ? "✅" : "❌"} | Suspicious: {recon.ai_suspicious ? "⚠️" : "✅"}</p>
                    <p>Manager: <span className={recon.manager_recommendation === "recommended" ? "text-green-600" : recon.manager_recommendation === "declined" ? "text-red-600" : "text-amber-600"}>{recon.manager_recommendation}</span></p>
                    <p>Finance: <span className={recon.finance_approval === "approved" ? "text-green-600" : recon.finance_approval === "declined" ? "text-red-600" : "text-amber-600"}>{recon.finance_approval}</span></p>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="mt-4 flex flex-wrap gap-2">
                {selectedTrip.status === "draft" && !selectedTrip.outstanding_balance_blocked && (
                  <Button size="sm" onClick={() => handleSubmit(selectedTrip.id)} className="bg-sky-600 hover:bg-sky-500 rounded-none text-white">
                    <Send className="w-4 h-4 mr-1" /> Submit to Travel Desk
                  </Button>
                )}
                {selectedTrip.status === "draft" && selectedTrip.outstanding_balance_blocked && !selectedTrip.override_letter_url && (
                  <Button size="sm" onClick={() => handleOverride(selectedTrip.id)} variant="outline" className="rounded-none border-amber-300 text-amber-600">
                    <Upload className="w-4 h-4 mr-1" /> Upload Override Letter
                  </Button>
                )}
                {selectedTrip.status === "draft" && selectedTrip.outstanding_balance_blocked && selectedTrip.override_letter_url && (
                  <Button size="sm" onClick={() => handleSubmit(selectedTrip.id)} className="bg-sky-600 hover:bg-sky-500 rounded-none text-white">
                    <Send className="w-4 h-4 mr-1" /> Submit (Overridden)
                  </Button>
                )}
                {selectedTrip.status === "travel_desk_booked" && (
                  <Button size="sm" onClick={openClaimDialog} className="bg-purple-600 hover:bg-purple-500 rounded-none text-white">
                    <FileText className="w-4 h-4 mr-1" /> Submit Expense Claim
                  </Button>
                )}
                {selectedTrip.status === "in_progress" && (
                  <Button size="sm" onClick={openReconDialog} className="bg-orange-600 hover:bg-orange-500 rounded-none text-white">
                    <Receipt className="w-4 h-4 mr-1" /> Submit Reconciliation
                  </Button>
                )}
                {["draft", "pending_travel_desk", "travel_desk_booked", "claim_submitted", "manager_recommended"].includes(selectedTrip.status) && (
                  <Button size="sm" onClick={() => handleCancel(selectedTrip.id)} variant="outline" className="rounded-none border-red-300 text-red-600">
                    <XCircle className="w-4 h-4 mr-1" /> Cancel Trip
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Expense Claim Dialog */}
      <Dialog open={claimOpen} onOpenChange={setClaimOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Submit Expense Claim</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input placeholder="GL Account (e.g. gl_travel)" value={claimForm.gl_account} onChange={(e) => setClaimForm(f => ({ ...f, gl_account: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Input type="number" placeholder="Daily S&T Amount" value={claimForm.daily_s_and_t_amount} onChange={(e) => setClaimForm(f => ({ ...f, daily_s_and_t_amount: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
            <Input type="number" placeholder="Total Claim Amount" value={claimForm.total_claim_amount} onChange={(e) => setClaimForm(f => ({ ...f, total_claim_amount: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
            <Button onClick={handleSubmitClaim} disabled={saving} className="w-full bg-purple-600 hover:bg-purple-500 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Claim"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reconciliation Dialog */}
      <Dialog open={reconOpen} onOpenChange={setReconOpen}>
        <DialogContent className="bg-white border-slate-200 rounded-none sm:max-w-md">
          <DialogHeader><DialogTitle className="font-['Barlow_Condensed'] text-xl">Submit Reconciliation</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <Input type="number" placeholder="Total Amount Used" value={reconForm.total_amount_used} onChange={(e) => setReconForm(f => ({ ...f, total_amount_used: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
            <Input type="number" placeholder="Total Amount Returned" value={reconForm.total_amount_returned} onChange={(e) => setReconForm(f => ({ ...f, total_amount_returned: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
            {selectedTrip?.trip_type === "foreign" && (
              <>
                <Input type="number" placeholder="Original Currency Amount (USD/EUR)" value={reconForm.original_currency_amount} onChange={(e) => setReconForm(f => ({ ...f, original_currency_amount: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
                <Input type="number" placeholder="Conversion Rate to ZAR" value={reconForm.conversion_rate} onChange={(e) => setReconForm(f => ({ ...f, conversion_rate: parseFloat(e.target.value) || 0 }))} className="rounded-none bg-app border-slate-300" />
              </>
            )}
            <Input placeholder="Proof of Return URL (document/image link)" value={reconForm.proof_of_return_url} onChange={(e) => setReconForm(f => ({ ...f, proof_of_return_url: e.target.value }))} className="rounded-none bg-app border-slate-300" />
            <Button onClick={handleSubmitRecon} disabled={saving} className="w-full bg-orange-600 hover:bg-orange-500 text-white rounded-none">
              {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : "Submit Reconciliation"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
